import type { Breadcrumb, ErrorEvent } from '@sentry/react';

export const REDACTED = '[redacted]';

// Whole HTTP payloads. Galen request and response bodies carry patient name, date of birth
// and MRN; the token-refresh request body is the refresh token. They arrive already
// serialised to a string, so redacting inside them is not possible — they go entirely.
const DROPPED_EXTRA_KEYS = new Set([
  'Request Data',
  'Response Data',
  'Request Params',
  'Response Headers',
  'Request Object',
]);

const DROPPED_CONTEXT_FIELDS: Record<string, readonly string[]> = {
  'Axios Error Details': ['requestData', 'responseData'],
};

// Matched against lowercased key names with non-alphanumerics stripped, so
// `X-Refresh-Token`, `x_refresh_token` and `xRefreshToken` all hit the same entry.
const REDACTED_KEY_PARTS = [
  'authorization',
  'xrefreshtoken',
  'refreshtoken',
  'accesstoken',
  'authtoken',
  'password',
  'firstname',
  'lastname',
  'fullname',
  'namelike',
  'dateofbirth',
  'dob',
  'patientid',
  'emailaddress',
  'email',
  'phone',
  'mrn',
];

const MAX_DEPTH = 6;

const normaliseKey = (key: string): string =>
  key.toLowerCase().replace(/[^a-z0-9]/g, '');

const isSensitiveKey = (key: string): boolean => {
  const normalised = normaliseKey(key);
  return REDACTED_KEY_PARTS.some((part) => normalised.includes(part));
};

// Patient search criteria — date of birth, patient id, name — travel in the query string.
export const stripQuery = (url: unknown): unknown => {
  if (typeof url !== 'string') return url;
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
};

// `ancestors` holds the current path only, so a cycle terminates while an object referenced
// twice is still redacted on each visit.
const redact = (
  value: unknown,
  depth: number,
  ancestors: Set<object>,
): unknown => {
  if (depth > MAX_DEPTH) return REDACTED;
  if (value === null || typeof value !== 'object') return value;
  if (ancestors.has(value)) return REDACTED;

  ancestors.add(value);
  try {
    if (Array.isArray(value)) {
      return value.map((item) => redact(item, depth + 1, ancestors));
    }

    const result: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) {
      result[key] = isSensitiveKey(key)
        ? REDACTED
        : redact(item, depth + 1, ancestors);
    }
    return result;
  } finally {
    ancestors.delete(value);
  }
};

const redactValue = (value: unknown): unknown => redact(value, 0, new Set());

const scrubBreadcrumb = (crumb: Breadcrumb): Breadcrumb => {
  if (!crumb.data) return crumb;
  const data = redactValue(crumb.data) as Record<string, unknown>;
  if ('url' in data) {
    data.url = stripQuery(data.url);
  }
  return { ...crumb, data };
};

const scrub = (event: ErrorEvent): ErrorEvent => {
  if (event.extra) {
    const extra: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(event.extra)) {
      if (DROPPED_EXTRA_KEYS.has(key)) continue;
      extra[key] = isSensitiveKey(key) ? REDACTED : redactValue(value);
    }
    if ('Request URL' in extra) {
      extra['Request URL'] = stripQuery(extra['Request URL']);
    }
    event.extra = extra;
  }

  if (event.contexts) {
    for (const [name, context] of Object.entries(event.contexts)) {
      if (!context) continue;
      const scrubbed = redactValue(context) as Record<string, unknown>;
      for (const field of DROPPED_CONTEXT_FIELDS[name] ?? []) {
        delete scrubbed[field];
      }
      if ('url' in scrubbed) scrubbed.url = stripQuery(scrubbed.url);
      event.contexts[name] = scrubbed;
    }
  }

  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.query_string;
    event.request.url = stripQuery(event.request.url) as string | undefined;
    if (event.request.headers) {
      event.request.headers = redactValue(event.request.headers) as Record<
        string,
        string
      >;
    }
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }

  return event;
};

// Fails closed: an event that cannot be scrubbed would carry the payloads this exists to
// remove, so it is dropped rather than sent.
export const scrubSentryEvent = (event: ErrorEvent): ErrorEvent | null => {
  try {
    return scrub(event);
  } catch {
    return null;
  }
};

// console breadcrumbs capture call arguments, which include whole axios error objects.
// xhr/fetch breadcrumbs are kept — the sequence of requests leading to a failure is what
// makes it diagnosable — minus the query string.
export const scrubSentryBreadcrumb = (crumb: Breadcrumb): Breadcrumb | null => {
  try {
    if (crumb.category === 'console') return null;
    return scrubBreadcrumb(crumb);
  } catch {
    return null;
  }
};
